/**
 * DATA LAYER (DL): RUN TO GOAL (TIRO A GOL V2)
 * Módulo de comunicación nativa HTTP / REST con el backend de Interaktia-V2 para Tiro a Gol.
 * Administra el CRUD completo de temas visuales multi-tenant, optimización de medios WebP,
 * asignación de temas por sala y control de partidas en tiempo real.
 * 
 * @module utilities/DL/RunToGoal
 */

/**
 * Resuelve la URL base de la API normalizando barras finales.
 * @returns {string} URL base del API.
 */
const getBaseApiUrl = () => {
    const raw = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    return raw.endsWith('/') ? raw.slice(0, -1) : raw;
};

/**
 * Resuelve la URL base para los endpoints de temas de Tiro a Gol.
 * @returns {string} Endpoint base de temas.
 */
const getThemeApiBaseUrl = () => {
    return `${getBaseApiUrl()}/v1/RunToGoal`;
};

/**
 * Resuelve la URL base para el motor de partidas en tiempo real RunToGoal V2.
 * @returns {string} Endpoint base del motor V2.
 */
const getV2ApiBaseUrl = () => {
    return `${getBaseApiUrl()}/v1/RunToGoalV2`;
};

// =========================================================================
// GESTIÓN DE TEMAS VISUALES (CRUD & ASIGNACIONES)
// =========================================================================

/**
 * Obtiene el catálogo de temas visuales disponibles con filtrado multi-tenant opcional por sala u operador.
 * @param {string|null} [roomId=null] - GUID de la sala.
 * @param {string|null} [operatorId=null] - GUID del operador.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const GetThemesDL = async (roomId = null, operatorId = null) => {
    try {
        const queryParams = new URLSearchParams();
        if (roomId) queryParams.append('roomId', String(roomId).trim());
        if (operatorId) queryParams.append('operatorId', String(operatorId).trim());

        const queryString = queryParams.toString();
        const url = `${getThemeApiBaseUrl()}/themes${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al consultar los temas de Tiro a Gol.`,
                data: []
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al consultar la lista de temas de Tiro a Gol.',
            data: []
        };
    }
};

/**
 * Obtiene el detalle completo de un tema específico por su ID.
 * @param {number|string} id - ID numérico del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetThemeByIdDL = async (id) => {
    try {
        if (!id) {
            return { success: false, message: 'El ID del tema es obligatorio.' };
        }

        const response = await fetch(`${getThemeApiBaseUrl()}/theme/${id}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al obtener el tema.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al obtener los detalles del tema.'
        };
    }
};

/**
 * Crea un nuevo tema personalizado de Tiro a Gol.
 * @param {Object} themeData - DTO con la configuración del nuevo tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const CreateThemeDL = async (themeData) => {
    try {
        const response = await fetch(`${getThemeApiBaseUrl()}/theme`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(themeData)
        });

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${response.status} al crear el tema.`
            };
        }

        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema creado exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al intentar crear el tema de Tiro a Gol.'
        };
    }
};

/**
 * Actualiza la configuración de un tema existente de Tiro a Gol.
 * @param {number|string} id - ID del tema a actualizar.
 * @param {Object} themeData - Nuevos datos del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const UpdateThemeDL = async (id, themeData) => {
    try {
        if (!id) {
            return { success: false, message: 'El ID del tema es obligatorio para actualizarlo.' };
        }

        const response = await fetch(`${getThemeApiBaseUrl()}/theme/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(themeData)
        });

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${response.status} al actualizar el tema.`
            };
        }

        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema actualizado correctamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al actualizar el tema de Tiro a Gol.'
        };
    }
};

/**
 * Guarda un tema visual creando uno nuevo o actualizando si ya cuenta con ID.
 * @param {Object} themeData - Configuración del tema (con o sin campo id).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const SaveThemeDL = async (themeData) => {
    const targetId = themeData?.id || themeData?.Id;
    if (targetId && Number(targetId) > 0) {
        return await UpdateThemeDL(targetId, themeData);
    }
    return await CreateThemeDL(themeData);
};

/**
 * Clona un tema existente como una nueva copia editable.
 * @param {number|string} id - ID del tema a duplicar.
 * @param {string|null} [newName=null] - Nombre de la copia resultante.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const CloneThemeDL = async (id, newName = null) => {
    try {
        if (!id) {
            return { success: false, message: 'El ID del tema origen es requerido.' };
        }

        const queryParam = newName ? `?newName=${encodeURIComponent(newName)}` : '';
        const response = await fetch(`${getThemeApiBaseUrl()}/theme/clone/${id}${queryParam}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newName: newName || '' })
        });

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${response.status} al clonar el tema.`
            };
        }

        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema clonado exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al clonar el tema.'
        };
    }
};

/**
 * Elimina un tema personalizado (no protegido como preset del sistema).
 * @param {number|string} id - ID del tema a eliminar.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const DeleteThemeDL = async (id) => {
    try {
        if (!id) {
            return { success: false, message: 'El ID del tema es obligatorio para su eliminación.' };
        }

        const response = await fetch(`${getThemeApiBaseUrl()}/theme/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${response.status} al eliminar el tema.`
            };
        }

        return {
            success: true,
            message: responseData.message || 'Tema eliminado exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al eliminar el tema.'
        };
    }
};

/**
 * Asigna un tema específico como el tema activo para una sala.
 * @param {number|string} themeId - Identificador del tema visual.
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const AssignThemeToRoomDL = async (themeId, roomId) => {
    try {
        if (!themeId || !roomId) {
            return { success: false, message: 'El themeId y roomId son obligatorios.' };
        }

        const response = await fetch(`${getThemeApiBaseUrl()}/theme/assign-room`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                themeId: Number(themeId),
                roomId: String(roomId).trim()
            })
        });

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${response.status} al asignar el tema a la sala.`
            };
        }

        return {
            success: true,
            message: responseData.message || 'Tema asignado exitosamente a la sala.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al asignar el tema a la sala.'
        };
    }
};

/**
 * Obtiene el tema activo correspondiente a una sala, o el preset por defecto si no hay asignación previa.
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetActiveThemeForRoomDL = async (roomId) => {
    try {
        if (!roomId) {
            return { success: false, message: 'El roomId es obligatorio.' };
        }

        const response = await fetch(`${getThemeApiBaseUrl()}/theme/room/${roomId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al obtener el tema activo.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al consultar el tema activo de la sala.'
        };
    }
};

/**
 * Sube, valida (Magic Bytes y Sanitización) y optimiza a WebP un recurso gráfico con SkiaSharp.
 * @param {File|Blob|FormData} file - Archivo de imagen o instancia de FormData.
 * @param {string|null} [category=null] - Categoría opcional del medio (cover, track, zones, outro, fan).
 * @param {string|null} [fieldName=null] - Campo visual opcional asignado.
 * @returns {Promise<{success: boolean, url?: string, message?: string, originalFileName?: string, width?: number, height?: number, fileSize?: number}>}
 */
export const UploadMediaDL = async (file, category = null, fieldName = null) => {
    try {
        if (!file) {
            return { success: false, message: 'No se ha proporcionado ningún archivo para subir.' };
        }

        let formData;
        if (file instanceof FormData) {
            formData = file;
        } else {
            formData = new FormData();
            formData.append('file', file);
            if (category) formData.append('category', category);
            if (fieldName) formData.append('fieldName', fieldName);
        }

        const response = await fetch(`${getThemeApiBaseUrl()}/upload-media`, {
            method: 'POST',
            body: formData
        });

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok || responseData.success === false) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${response.status} al procesar la subida del medio.`
            };
        }

        return {
            success: true,
            url: responseData.url,
            message: responseData.message || 'Medio subido y optimizado exitosamente.',
            originalFileName: responseData.originalFileName,
            width: responseData.width,
            height: responseData.height,
            fileSize: responseData.fileSize
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al subir el archivo multimedia.'
        };
    }
};

/**
 * Ejecuta el seed idempotente de los presets de sistema de Tiro a Gol en el backend.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const SeedPresetsDL = async () => {
    try {
        const response = await fetch(`${getThemeApiBaseUrl()}/seed-presets`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${response.status} al registrar los presets de sistema.`
            };
        }

        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Presets del sistema registrados exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al procesar el registro de presets.'
        };
    }
};

// =========================================================================
// MOTOR DE PARTIDAS EN TIEMPO REAL V2
// =========================================================================

/**
 * Solicita la creación de una nueva partida V2 de Tiro a Gol con soporte de ThemeId y RoomId.
 * @param {Object} matchConfiguration - Configuración de la partida.
 * @param {number} [matchConfiguration.matchDurationSeconds=45] - Duración de la carrera en segundos.
 * @param {number} [matchConfiguration.tapsPerTick=10] - Cantidad de taps por paquete.
 * @param {number} [matchConfiguration.minimumUpdateMilliseconds=100] - Intervalo de actualización a pantalla.
 * @param {number|null} [matchConfiguration.themeId=null] - ID del tema visual a proyectar.
 * @param {string|null} [matchConfiguration.roomId=null] - GUID de la sala.
 * @param {string|null} [matchConfiguration.operatorId=null] - GUID del operador.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const CreateRunToGoalV2MatchDL = async (matchConfiguration = {}) => {
    try {
        const payload = {
            matchDurationSeconds: Number(matchConfiguration.matchDurationSeconds) || 45,
            tapsPerTick: Number(matchConfiguration.tapsPerTick) || 10,
            minimumUpdateMilliseconds: Number(matchConfiguration.minimumUpdateMilliseconds) || 100,
            themeId: matchConfiguration.themeId ? Number(matchConfiguration.themeId) : null,
            roomId: matchConfiguration.roomId || null,
            operatorId: matchConfiguration.operatorId || null
        };

        const response = await fetch(`${getV2ApiBaseUrl()}/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${response.status} al crear la partida V2.`
            };
        }

        return {
            success: true,
            data: responseData,
            message: responseData.message || 'Partida V2 creada exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al crear la partida V2 de Tiro a Gol.'
        };
    }
};

/**
 * Da la orden al servidor para iniciar el reloj oficial de la partida.
 * @param {string} matchIdentifier - Identificador único de la partida activa.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const StartRunToGoalV2MatchDL = async (matchIdentifier) => {
    try {
        if (!matchIdentifier) {
            return { success: false, message: 'El identificador de la partida es obligatorio.' };
        }

        const response = await fetch(`${getV2ApiBaseUrl()}/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ matchIdentifier: String(matchIdentifier).trim() })
        });

        const responseData = await response.json().catch(() => ({}));
        return {
            success: response.ok,
            message: responseData.message || (response.ok ? 'Reloj iniciado.' : 'Error al iniciar reloj.')
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al iniciar el reloj de la partida V2.'
        };
    }
};

/**
 * Reinicia la partida en tiempo real a estado de espera (Waiting).
 * @param {string} matchIdentifier - Identificador único de la partida activa.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const ResetRunToGoalV2MatchDL = async (matchIdentifier) => {
    try {
        if (!matchIdentifier) {
            return { success: false, message: 'El identificador de la partida es obligatorio.' };
        }

        const response = await fetch(`${getV2ApiBaseUrl()}/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ matchIdentifier: String(matchIdentifier).trim() })
        });

        const responseData = await response.json().catch(() => ({}));
        return {
            success: response.ok,
            message: responseData.message || (response.ok ? 'Partida reiniciada.' : 'Error al reiniciar partida.')
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al reiniciar la partida V2.'
        };
    }
};

/**
 * Finaliza la partida anticipadamente, calculando al ganador con los taps actuales y persistiendo en BD.
 * @param {string} matchIdentifier - Identificador único de la partida activa.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const FinishRunToGoalV2MatchEarlyDL = async (matchIdentifier) => {
    try {
        if (!matchIdentifier) {
            return { success: false, message: 'El identificador de la partida es obligatorio.' };
        }

        const response = await fetch(`${getV2ApiBaseUrl()}/finish-early`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ matchIdentifier: String(matchIdentifier).trim() })
        });

        const responseData = await response.json().catch(() => ({}));
        return {
            success: response.ok,
            message: responseData.message || (response.ok ? 'Partida terminada anticipadamente.' : 'Error al terminar partida.')
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al terminar la partida anticipadamente.'
        };
    }
};

/**
 * Dispara la señal para mostrar la portada de Tiro a Gol en pantalla gigante.
 * @param {string|null} [roomId=null] - GUID de la sala.
 * @param {number|null} [themeId=null] - ID del tema.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const ShowRunToGoalV2FrontPageDL = async (roomId = null, themeId = null) => {
    try {
        const response = await fetch(`${getBaseApiUrl()}/admin/RunToGoal/ShowFrontPage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId, themeId })
        });
        return { success: response.ok };
    } catch (error) {
        return { success: false, message: 'Error de red al proyectar la portada.' };
    }
};

/**
 * Envío silencioso de taps acumulados por aficionados con anti-cheat clamping en backend.
 * @param {Object} tapData - Datos de los taps.
 * @param {string} tapData.matchIdentifier - ID de la partida activa.
 * @param {string} tapData.zoneIdentifier - Zona asignada del usuario.
 * @param {number} tapData.tapsObtained - Cantidad de pulsaciones en el lote.
 * @returns {Promise<{success: boolean}>}
 */
export const AddTapsToRunToGoalV2ZoneDL = async ({ matchIdentifier, zoneIdentifier, tapsObtained }) => {
    try {
        const response = await fetch(`${getV2ApiBaseUrl()}/add-taps`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                matchIdentifier,
                zoneIdentifier,
                tapsObtained: Number(tapsObtained) || 0
            })
        });

        return { success: response.ok };
    } catch (networkError) {
        return { success: false };
    }
};

// =========================================================================
// ALIASES PARA COMPATIBILIDAD CON V2 Y CONVENCIONES DL
// =========================================================================

export const GetThemes = GetThemesDL;
export const GetThemeById = GetThemeByIdDL;
export const CreateTheme = CreateThemeDL;
export const UpdateTheme = UpdateThemeDL;
export const SaveTheme = SaveThemeDL;
export const CloneTheme = CloneThemeDL;
export const DeleteTheme = DeleteThemeDL;
export const AssignThemeToRoom = AssignThemeToRoomDL;
export const GetActiveThemeForRoom = GetActiveThemeForRoomDL;
export const UploadMedia = UploadMediaDL;
export const SeedPresets = SeedPresetsDL;
export const CreateRunToGoalV2Match = CreateRunToGoalV2MatchDL;
export const StartRunToGoalV2Match = StartRunToGoalV2MatchDL;
export const ResetRunToGoalV2Match = ResetRunToGoalV2MatchDL;
export const FinishRunToGoalV2MatchEarly = FinishRunToGoalV2MatchEarlyDL;
export const ShowRunToGoalV2FrontPage = ShowRunToGoalV2FrontPageDL;
export const AddTapsToRunToGoalV2Zone = AddTapsToRunToGoalV2ZoneDL;
